// ============================================================================
// "Already on file?" — the one door's answer when a sub being added looks like
// one we already have (Ben, 2026-10-28, "D1. 1" on "Stopping repeat subs").
//
// add_subcontractor() checks the same 10-digit phone, or a similar name / owner /
// legal name, and refuses with HINT TCS_SUB_ALREADY_ON_FILE and the matches as
// JSON in DETAIL. Nothing is saved. Every screen that adds a sub shows the same
// notice: who was found, "Open them", and "It's a different company. Add anyway",
// which repeats the save with p_add_anyway. The rule lives in the database only;
// this file only reads its answer.
// ============================================================================

import { useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';

export const ALREADY_ON_FILE_HINT = 'TCS_SUB_ALREADY_ON_FILE';

export interface OnFileMatch {
  provider_id: string;
  name: string;
  status: string;
  markets: string[];
  added: string;
  buildings: number;
  why: string;
}

export class AlreadyOnFileError extends Error {
  readonly matches: OnFileMatch[];
  constructor(message: string, matches: OnFileMatch[]) {
    super(message);
    this.name = 'AlreadyOnFileError';
    this.matches = matches;
  }
}

/** The door's refusal → AlreadyOnFileError, or null for any other error. */
export function alreadyOnFileFrom(error: unknown): AlreadyOnFileError | null {
  if (error instanceof AlreadyOnFileError) return error;
  const e = error as { hint?: string | null; details?: string | null; message?: string | null } | null;
  if (!e || e.hint !== ALREADY_ON_FILE_HINT) return null;
  let matches: OnFileMatch[] = [];
  try {
    const parsed = JSON.parse(e.details ?? '[]');
    if (Array.isArray(parsed)) matches = parsed as OnFileMatch[];
  } catch {
    matches = [];
  }
  return new AlreadyOnFileError(e.message ?? 'Already on file.', matches);
}

const STAGE_LABEL: Record<string, string> = { Meet: 'Interviewing', Ready: 'Bench' };

function describe(m: OnFileMatch): string {
  const markets = (m.markets ?? []).map((x) => x.replace(/\s*Metro$/, '')).join(' + ');
  const where = m.buildings > 0 ? `on ${m.buildings} building${m.buildings === 1 ? '' : 's'}` : null;
  return [STAGE_LABEL[m.status] ?? m.status, markets || null, where, `added ${m.added}`].filter(Boolean).join(', ');
}

/**
 * The notice. `onOpen` runs when "Open them" is pressed (close a sheet first);
 * `openInNewTab` keeps a half-filled form where it is. `instead` is an extra
 * line for a form where the right move is to pick the existing sub there.
 */
export function AlreadyOnFileNotice({
  found,
  onAddAnyway,
  busy = false,
  onOpen,
  openInNewTab = false,
  instead,
}: {
  found: AlreadyOnFileError;
  onAddAnyway: () => void;
  busy?: boolean;
  onOpen?: () => void;
  openInNewTab?: boolean;
  instead?: string;
}) {
  const list = found.matches.length > 0 ? found.matches : null;
  return (
    <div role="alert" className="rounded-lg border border-st-warn-border bg-st-warn-bg p-3 text-sm text-st-warn-fg">
      <div className="font-semibold">Already on file?</div>
      {list ? (
        <ul className="mt-1 space-y-1.5">
          {list.map((m) => (
            <li key={m.provider_id}>
              <span className="font-medium">{m.name}</span>: {describe(m)}. <span className="italic">{m.why[0].toUpperCase() + m.why.slice(1)}.</span>{' '}
              <Link
                to={`/people/subcontractors/${encodeURIComponent(m.provider_id)}`}
                onClick={onOpen}
                target={openInNewTab ? '_blank' : undefined}
                rel={openInNewTab ? 'noreferrer' : undefined}
                className="font-semibold underline underline-offset-2"
              >
                Open them
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1">{found.message}</p>
      )}
      {instead && <p className="mt-1.5">{instead}</p>}
      <button
        type="button"
        disabled={busy}
        onClick={onAddAnyway}
        className="mt-2 min-h-[44px] w-full rounded-lg border border-st-warn-border bg-tcs-raised px-3 text-sm font-semibold text-tcs-text disabled:opacity-60"
      >
        {busy ? 'Adding…' : "It's a different company. Add anyway"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ask AI: the save runs in the copilot hook, the card only sees "Do it". The
// hook records the refusal here by proposal, and the card reads it back.
// ---------------------------------------------------------------------------
const byProposal = new Map<string, AlreadyOnFileError>();
const listeners = new Set<() => void>();

export function rememberOnFile(actionId: string, found: AlreadyOnFileError | null): void {
  if (found) byProposal.set(actionId, found);
  else byProposal.delete(actionId);
  listeners.forEach((l) => l());
}

export function useOnFileFor(actionId: string): AlreadyOnFileError | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => byProposal.get(actionId) ?? null,
  );
}
